import { useCallback, useEffect, useState } from "react";
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { useSelector } from 'react-redux';
import { selectAccountData } from "../redux/main/accountSlice";

export const VehicleSelect = ({ selectedOptions, setSelectedOptions, isMulti = true, placeholder = "Seleziona veicoli...", menuPortalTarget, menuPosition, menuPlacement }) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const token = localStorage.getItem('installamelo-auth-token');
    const accountData = useSelector(selectAccountData);
    const accountId = accountData?._id;

    // Funzione per eseguire la chiamata API
    const fetchVehicles = async (searchText) => {
        if (!accountId) return;
        
        setIsLoading(true);
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/vehicles/list?accountId=${accountId}&internal=true`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                }
            });
            
            if (!response.ok) {
                throw new Error(`Errore API: ${response.status}`);
            }
            
            const data = await response.json();
            
            // L'API /vehicles/list restituisce { data: [...] }
            const items = data.data || [];
            
            // Filtra per searchText se presente (il filtro lato server non funziona bene)
            let filteredItems = items;
            if (searchText && searchText.trim() !== '') {
                const searchLower = searchText.toLowerCase();
                filteredItems = items.filter(v => 
                    (v.model && v.model.toLowerCase().includes(searchLower)) ||
                    (v.plateNumber && v.plateNumber.toLowerCase().includes(searchLower))
                );
            }
            
            // Trasforma i dati nel formato richiesto da react-select
            // Nota: l'API restituisce 'id' invece di '_id'
            const formattedOptions = filteredItems.map(vehicle => ({
                value: vehicle.id || vehicle._id,
                label: `${vehicle.model || ''} ${vehicle.plateNumber ? `(${vehicle.plateNumber})` : ''}`.trim(),
                data: vehicle
            }));
            
            setOptions(formattedOptions);
        } catch (error) {
            console.error('Errore nel caricamento dei veicoli:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Debounce per evitare troppe chiamate API durante la digitazione
    const debouncedFetchVehicles = useCallback(
        debounce(fetchVehicles, 300),
        [accountId, token]
    );

    // Carica le opzioni iniziali
    useEffect(() => {
        fetchVehicles('');
    }, [accountId, token]);

    // Gestisce la modifica dell'input di ricerca
    const handleInputChange = (newValue) => {
        setInputValue(newValue);
        debouncedFetchVehicles(newValue);
    };

    // Stile personalizzato per react-select
    const customStyles = {
        control: (base, state) => ({
            ...base,
            borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
            boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
            '&:hover': {
                borderColor: state.isFocused ? '#3182ce' : '#cbd5e0'
            },
            borderRadius: '0.375rem',
            backgroundColor: state.isDisabled ? '#f7fafc' : 'white',
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected 
                ? '#3182ce' 
                : state.isFocused 
                    ? '#ebf8ff' 
                    : base.backgroundColor,
            color: state.isSelected ? 'white' : '#4a5568',
            ':active': {
                backgroundColor: state.isSelected ? '#3182ce' : '#e6f7ff'
            },
            padding: '8px 12px'
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#e6fffa',
            borderRadius: '0.25rem',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#2c7a7b',
            fontWeight: 500,
        }),
        multiValueRemove: (base) => ({
            ...base,
            color: '#2c7a7b',
            ':hover': {
                backgroundColor: '#b2f5ea',
                color: '#285e61'
            }
        }),
        menu: (base) => ({
            ...base,
            borderRadius: '0.375rem',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            zIndex: 10000000,
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 10000000,
        }),
        noOptionsMessage: (base) => ({
            ...base,
            fontSize: '0.875rem',
            color: '#718096'
        })
    };

    return (
        <Select
            isMulti={isMulti}
            options={options}
            value={selectedOptions}
            onChange={setSelectedOptions}
            onInputChange={handleInputChange}
            inputValue={inputValue}
            isLoading={isLoading}
            placeholder={placeholder}
            noOptionsMessage={() => "Nessun veicolo trovato"}
            loadingMessage={() => "Caricamento veicoli..."}
            styles={customStyles}
            isClearable
            closeMenuOnSelect={!isMulti}
            classNamePrefix="vehicle-select"
            menuPortalTarget={menuPortalTarget !== undefined ? menuPortalTarget : document.body}
            menuPosition={menuPosition || "fixed"}
            menuPlacement={menuPlacement || "auto"}
        />
    );
};

export default VehicleSelect;
